import { ProfileStrength } from "@/types/familyStats";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";

/** Short time-of-day label for the experienced-dashboard header ("Evening, {name}"). */
export const getTimeBasedGreetingPeriod = (
  hour: number = new Date().getHours()
): "morning" | "afternoon" | "evening" | "night" => {
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 21) return "evening";
  return "night";
};

// Storage key for dashboard message cache
const DASHBOARD_MESSAGE_CACHE_KEY = "dashboard_message_cache";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface MessageCache {
  message: string;
  timestamp: number;
}

export const useDynamicDashboardMessage = (
  profileStrength: ProfileStrength,
  userName: string
) => {
  const { t, ready } = useTranslation();
  const [message, setMessage] = useState<string>("");
  const [messageKey, setMessageKey] = useState<number>(0);
  const hasSetInitialMessage = useRef(false);

  useEffect(() => {
    // Wait until translations are ready to avoid flickering
    if (!ready) return;

    // Only set message once initially, then only when dependencies change
    if (!hasSetInitialMessage.current) {
      hasSetInitialMessage.current = true;
      const initialMessage = generateMessage(profileStrength, userName, t);
      setMessage(initialMessage);
      setMessageKey(0); // Initial key
      return;
    }

    // Generate new message only when dependencies change
    const newMessage = generateMessage(profileStrength, userName, t);
    setMessage(newMessage);
    setMessageKey((prev) => prev + 1); // Increment key to trigger animation
  }, [profileStrength, userName, t, ready]);

  return { message, messageKey };
};

// Helper function to generate message with localStorage cache
const generateMessage = (
  profileStrength: ProfileStrength,
  userName: string,
  t: any
) => {
  // Check if we have a cached message that's still valid (only check timestamp)
  const cachedData = getCachedMessage();
  if (cachedData) {
    return cachedData;
  }

  // Generate new message
  const messages = t(`dashboard.dynamicMessages.${profileStrength}`, {
    returnObjects: true,
  }) as string[];

  if (!Array.isArray(messages) || messages.length === 0) {
    const fallbackMessage = t("dashboard.dynamicMessages.low.0", {
      name: userName,
    });
    cacheMessage(fallbackMessage);
    return fallbackMessage;
  }

  const randomIndex = Math.floor(Math.random() * messages.length);
  const newMessage = t(
    `dashboard.dynamicMessages.${profileStrength}.${randomIndex}`,
    { name: userName }
  );

  // Cache the new message
  cacheMessage(newMessage);

  return newMessage;
};

// Get cached message if it's still valid (only check timestamp)
const getCachedMessage = (): string | null => {
  try {
    const cached = localStorage.getItem(DASHBOARD_MESSAGE_CACHE_KEY);
    if (!cached) return null;

    const cacheData: MessageCache = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid (24 hours) - ignore user/profile changes
    if (now - cacheData.timestamp < CACHE_DURATION) {
      return cacheData.message;
    }

    // Cache expired, remove it
    localStorage.removeItem(DASHBOARD_MESSAGE_CACHE_KEY);
    return null;
  } catch (error) {
    console.warn("Error reading dashboard message cache:", error);
    return null;
  }
};

// Cache message with timestamp
const cacheMessage = (message: string): void => {
  try {
    const cacheData: MessageCache = {
      message,
      timestamp: Date.now(),
    };

    localStorage.setItem(
      DASHBOARD_MESSAGE_CACHE_KEY,
      JSON.stringify(cacheData)
    );
  } catch (error) {
    console.warn("Error caching dashboard message:", error);
  }
};
